import { Footer, Header, SEO } from "@/components";
import SEOConfig from "@/common/seo.config";
import BlogCard from "@/components/main/blog-card";
import Link from "next/link";
import { useMemo, useState } from "react";
import { PageHero } from "@/components/main/landing/landing-ui";
import { fetchBlogList } from "@/lib/blogs";
import {
  buildBreadcrumbList,
  buildCollectionPage,
  buildItemListSchema,
  pageSchemas,
} from "@/lib/schema";

const Blog = ({ blogs = [] }) => {
  const allBlogs = blogs;
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(9);
  const blogsPerPage = 9;

  const structuredData = useMemo(
    () =>
      pageSchemas([
        buildCollectionPage({
          path: "/blogs",
          name: SEOConfig.blog.pageTitle,
          description: SEOConfig.blog.pageDescription,
          type: "CollectionPage",
        }),
        buildItemListSchema({
          name: "Nivalabs articles",
          path: "/blogs",
          items: allBlogs.map((blog) => ({
            name: blog.title,
            path: `/blogs/${blog.slug}`,
          })),
        }),
        buildBreadcrumbList([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blogs" },
        ]),
      ]),
    [allBlogs],
  );

  const categories = useMemo(() => {
    const dynamicCategories = allBlogs
      .map((blog) => blog?.category?.title)
      .filter(Boolean);
    return ["All", ...Array.from(new Set(dynamicCategories))];
  }, [allBlogs]);

  const filteredBlogs = useMemo(() => {
    return allBlogs.filter((blog) => {
      const title = String(blog?.title || "").toLowerCase();
      const category = String(blog?.category?.title || "").toLowerCase();
      const matchesQuery = query
        ? title.includes(query.toLowerCase()) ||
          category.includes(query.toLowerCase())
        : true;
      const matchesCategory =
        activeCategory === "All" || blog?.category?.title === activeCategory;
      return matchesQuery && matchesCategory;
    });
  }, [allBlogs, activeCategory, query]);

  const visibleBlogs = filteredBlogs.slice(0, visibleCount);
  const hasMore = visibleCount < filteredBlogs.length;

  const onFilterChange = (nextCategory) => {
    setActiveCategory(nextCategory);
    setVisibleCount(blogsPerPage);
  };

  const onSearchChange = (event) => {
    setQuery(event.target.value);
    setVisibleCount(blogsPerPage);
  };

  return (
    <>
      <SEO {...SEOConfig.blog} structuredData={structuredData} />
      <Header />
      <main>
        <PageHero
          compact
          title="From the field"
          lead="Practical notes on agents, LLM applications, and automation from production work."
        />

        <section className="pb-14 md:pb-20">
          <div className="container max-w-7xl">
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-[0_12px_32px_-22px_rgba(15,28,26,0.18)] md:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <input
                  type="text"
                  value={query}
                  onChange={onSearchChange}
                  placeholder="Search by title or topic..."
                  className="w-full rounded-xl border border-line bg-canvas px-4 py-3 text-ink placeholder:text-ink-soft focus:border-[#1c8c80] focus:outline-none lg:max-w-md"
                />
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => onFilterChange(category)}
                      className={`rounded-full px-3 py-2 text-xs transition-all md:text-sm ${
                        activeCategory === category
                          ? "bg-[#1c8c80] text-white"
                          : "border border-line bg-canvas text-ink-muted hover:border-[#5aada4]"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {visibleBlogs.length === 0 && (
              <div className="py-12 text-center text-ink-muted">
                No blogs found for this search.
              </div>
            )}

            {visibleBlogs.length > 0 && (
              <>
                <div className="my-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {visibleBlogs.map((blog) => (
                    <BlogCard key={blog.slug} blog={blog} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleCount((prev) => prev + blogsPerPage)
                      }
                      className="rounded-full border border-[#1c8c80] bg-surface px-6 py-3 text-ink transition-colors hover:bg-[#1c8c80] hover:text-white"
                    >
                      Load more articles
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {allBlogs.length > 0 && (
          <section className="border-t border-line pb-14 pt-14 md:pb-20 md:pt-20">
            <div className="container max-w-7xl">
              <h2 className="m-0 text-2xl font-semibold text-ink md:text-3xl">
                All articles
              </h2>
              <p className="mt-3 max-w-2xl text-ink-muted">
                Every article we have published, newest first.
              </p>
              <ul className="mt-8 grid list-none grid-cols-1 gap-x-8 gap-y-1 p-0 md:grid-cols-2">
                {allBlogs.map((blog) => (
                  <li key={blog.slug} className="border-b border-line">
                    <Link
                      href={`/blogs/${blog.slug}`}
                      className="flex flex-col gap-1 py-3 text-ink no-underline transition-colors hover:text-[#1c8c80] sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                    >
                      <span className="font-medium">{blog.title}</span>
                      {blog.category?.title && (
                        <span className="shrink-0 text-xs uppercase tracking-[0.08em] text-ink-soft">
                          {blog.category.title}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

export async function getStaticProps() {
  const blogs = await fetchBlogList();

  return {
    props: { blogs },
    revalidate: 300,
  };
}

export default Blog;
